import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox } from 'component/basics';
import { PaginationRow } from 'component/dashboard/pagination-row';
import type { TableColumn } from 'component/dashboard/table-grid';
import { TableGrid } from 'component/dashboard/table-grid';
import { SearchField } from 'component/form';
import { Heading } from 'component/text';
import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useSessionDataValues } from 'util/datastore';
import { useDebouncedValue } from 'util/hooks';
import { type TLabel, t } from 'util/stdtypes';

/**
 * A filter widget plus the part of the table's query it controls. `toQuery`
 * turns `filterState`, whatever the widget stored in session data under
 * `sessionKey`, into that part: an on/off flag, a chosen value, or a set of
 * them. The table reads that key on the filter's behalf; a filter that depends
 * on no session state omits it and is passed `undefined`.
 */
export type TableFilter<Query> = {
    key: string;
    widget: React.ComponentType;
    sessionKey?: string;
    toQuery: (filterState: unknown) => Partial<Query>;
};

/**
 * What the table's controls currently ask for: the filters' combined `query`,
 * the `search` text, and which page. Made by `usePaginatedTableControls`; pass
 * these into your own fetch, and the object itself to `PaginatedTable`.
 */
export type PaginatedTableControls<Query> = {
    filters: TableFilter<Query>[];
    query: Partial<Query>;
    search: string | undefined;
    pageIndex: number;
    pageSize: number;
    searchText: string;
    setSearchText: (text: string) => void;
    setPageIndex: (index: number) => void;
};

const PAGE_SIZE = 10;

/**
 * The search, filter and page state behind a `PaginatedTable`, 10 rows a page.
 * `search` is the search box's text once typing pauses, or `undefined` while
 * the box is empty. Changing the search or a filter goes back to the first
 * page.
 */
export function usePaginatedTableControls<Query>({ filters = [] }: { filters?: TableFilter<Query>[] }): PaginatedTableControls<Query> {
    const [searchText, setSearchText] = useState('');
    const search = useDebouncedValue(searchText) || undefined;
    const filterStateByKey = useSessionDataValues(filters.flatMap((filter) => filter.sessionKey ?? []));

    const query: Partial<Query> = Object.assign(
        {},
        ...filters.map(({ sessionKey, toQuery }) => toQuery(sessionKey ? filterStateByKey[sessionKey] : undefined)),
    );
    const queryKey = JSON.stringify({ query, search });
    const [page, setPage] = useState({ queryKey, index: 0 });
    const pageIndex = page.queryKey === queryKey ? page.index : 0;
    const setPageIndex = useCallback((index: number) => setPage({ queryKey, index }), [queryKey]);

    return { filters, query, search, pageIndex, pageSize: PAGE_SIZE, searchText, setSearchText, setPageIndex };
}

export type PaginatedTableProps<Row, Query> = {
    titleLabel: TLabel;
    columns: TableColumn<Row>[];
    itemKey: (item: Row) => string;
    controls: PaginatedTableControls<Query>;
    items: Row[] | undefined;
    totalCount: number | undefined;
    isLoading?: boolean;
    searchable?: boolean;
    rightWidgets?: React.ComponentType[];
    emptyWidget?: React.ComponentType;
};

/**
 * A titled table with filter widgets, an optional search box, and a pagination
 * row. The caller fetches the page the controls ask for and passes in its rows:
 *
 * ```tsx
 * const controls = usePaginatedTableControls({ filters });
 * const { items, totalCount, isLoading } = useThingsPage({
 *     query: controls.query,
 *     search: controls.search,
 *     pageIndex: controls.pageIndex,
 *     pageSize: controls.pageSize,
 * });
 * return (
 *     <PaginatedTable
 *         controls={controls}
 *         items={items}
 *         totalCount={totalCount}
 *         isLoading={isLoading}
 *         ...
 *     />
 * );
 * ```
 *
 * `totalCount` counts the matching rows across every page. For a list already
 * in memory, `pageOfList` gives `items` and `totalCount`. `isLoading` defaults
 * to whether `items` is still `undefined`.
 */
export function PaginatedTable<Row, Query>({
    titleLabel,
    columns,
    itemKey,
    controls,
    items,
    totalCount = 0,
    isLoading = items === undefined,
    searchable,
    rightWidgets = [],
    emptyWidget: EmptyWidget,
}: PaginatedTableProps<Row, Query>) {
    const s = usePaginatedTableStyles();
    const { filters, pageIndex, pageSize, searchText, setSearchText, setPageIndex } = controls;

    // A page can empty out under the viewer, e.g. when its last row is removed.
    const lastPageIndex = Math.max(0, Math.ceil(totalCount / pageSize) - 1);
    useEffect(() => {
        if (!isLoading && pageIndex > lastPageIndex) setPageIndex(lastPageIndex);
    }, [isLoading, pageIndex, lastPageIndex, setPageIndex]);

    return (
        <View style={s.table}>
            <HorizBox center spread>
                <Heading label={titleLabel} type='large' weight='medium' />
                {searchable && (
                    <SearchField
                        onChange={setSearchText}
                        placeholderLabel={t('space.table.search', 'Search...')}
                        size='small'
                        value={searchText}
                    />
                )}
            </HorizBox>
            <HorizBox center spread>
                <HorizBox gap={16}>
                    {filters.map((filter) => (
                        <filter.widget key={filter.key} />
                    ))}
                </HorizBox>
                <HorizBox>
                    {rightWidgets.map((widget, i) =>
                        // eslint-disable-next-line react/no-array-index-key
                        React.createElement(widget, { key: i }),
                    )}
                </HorizBox>
            </HorizBox>
            <TableGrid
                columns={columns}
                emptyWidget={EmptyWidget && <EmptyWidget />}
                itemKey={itemKey}
                itemList={items ?? []}
                loading={isLoading}
            />
            {totalCount > pageSize && (
                <PaginationRow currentPage={pageIndex} onPageChange={setPageIndex} pageSize={pageSize} totalItems={totalCount} />
            )}
        </View>
    );
}

/**
 * The page of an in-memory list that `controls` ask for, keeping the rows
 * `matches` accepts. For a table whose data source can't filter or page on the
 * server. `itemList` is `undefined` while it loads.
 */
export function pageOfList<Row, Query>({
    itemList,
    controls,
    matches,
}: {
    itemList: Row[] | undefined;
    controls: Pick<PaginatedTableControls<Query>, 'query' | 'search' | 'pageIndex' | 'pageSize'>;
    matches: (args: { item: Row; query: Partial<Query>; search: string | undefined }) => boolean;
}): { items: Row[] | undefined; totalCount: number | undefined } {
    if (!itemList) return { items: undefined, totalCount: undefined };
    const { query, search, pageIndex, pageSize } = controls;
    const matching = itemList.filter((item) => matches({ item, query, search }));
    return { items: matching.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize), totalCount: matching.length };
}

const usePaginatedTableStyles = createThemedStyles((tokens) => ({
    table: {
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderColor: tokens.colors.colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
}));
